/**
 * Chrome Notification Service
 * Provides a wrapper around Chrome's notifications API for the extension
 * and Web Notifications API for browser contexts
 */

// Check if we're running in a Chrome extension context
const isExtension = !!(window.chrome && chrome.runtime && chrome.runtime.id);

// Check if browser notifications are supported
const isBrowserNotificationSupported = 'Notification' in window;

// Define Chrome notification types
type ChromeNotificationType = 'basic' | 'image' | 'list' | 'progress';

// Define notification options type
export interface NotificationOptions {
  title: string;
  message: string;
  iconUrl?: string;
  type?: ChromeNotificationType;
  buttons?: Array<{title: string; iconUrl?: string}>;
  imageUrl?: string;
  priority?: 0 | 1 | 2;
  onButtonClick?: (buttonIndex: number) => void;
  onClicked?: () => void;
  onClosed?: () => void;
}

// Chrome-specific notification options
interface ChromeNotificationOptions {
  type: ChromeNotificationType;
  title: string;
  message: string;
  iconUrl: string;
  buttons?: Array<{title: string; iconUrl?: string}>;
  imageUrl?: string;
  priority?: number;
  requireInteraction?: boolean;
}

// Default icon URL
const DEFAULT_ICON_URL = chrome.runtime?.getURL?.('icon-128.png') || '/icon-128.png';

// Store callback functions
const buttonCallbacks: Record<string, (buttonIndex: number) => void> = {};
const clickCallbacks: Record<string, () => void> = {};
const closeCallbacks: Record<string, () => void> = {};

// Request notification permission for browser notifications
export const requestNotificationPermission = async (): Promise<NotificationPermission> => {
  if (!isBrowserNotificationSupported) {
    console.warn('Browser notifications not supported');
    return 'denied';
  }

  if (Notification.permission === 'granted') {
    return 'granted';
  }

  if (Notification.permission === 'denied') {
    return 'denied';
  }

  // Request permission
  const permission = await Notification.requestPermission();
  return permission;
};

// Show browser notification using Web Notifications API
const showBrowserNotification = (options: NotificationOptions): Promise<string | null> => {
  return new Promise(async (resolve) => {
    if (!isBrowserNotificationSupported) {
      console.warn('Browser notifications not supported');
      resolve(null);
      return;
    }

    // Request permission if needed
    const permission = await requestNotificationPermission();
    if (permission !== 'granted') {
      console.warn('Notification permission not granted');
      resolve(null);
      return;
    }

    const notificationId = `browser-notification-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    
    try {
      const notification = new Notification(options.title, {
        body: options.message,
        icon: options.iconUrl || DEFAULT_ICON_URL,
        tag: notificationId,
        requireInteraction: true,
        silent: false
      });

      // Set up event listeners
      if (options.onClicked) {
        notification.onclick = () => {
          options.onClicked?.();
          notification.close();
        };
      }

      if (options.onClosed) {
        notification.onclose = () => {
          options.onClosed?.();
        };
      }

      // Auto-close after 10 seconds if not requiring interaction
      setTimeout(() => {
        notification.close();
      }, 10000);

      resolve(notificationId);
    } catch (error) {
      console.error('Error creating browser notification:', error);
      resolve(null);
    }
  });
};

// Add event listeners for chrome notifications if in extension context
if (isExtension) {
  // Check if notifications API is available
  if (!chrome.notifications) {
    console.warn(
      'Running in extension context but chrome.notifications is not available. ' +
      'Make sure you have the "notifications" permission in your manifest.json.'
    );
  } else {
    // Listen for button clicks
    chrome.notifications.onButtonClicked.addListener((notificationId, buttonIndex) => {
      if (buttonCallbacks[notificationId]) {
        buttonCallbacks[notificationId](buttonIndex);
        delete buttonCallbacks[notificationId];
      }
    });

    // Listen for notification clicks
    chrome.notifications.onClicked.addListener((notificationId) => {
      if (clickCallbacks[notificationId]) {
        clickCallbacks[notificationId]();
        delete clickCallbacks[notificationId];
      }
    });

    // Listen for notification close
    chrome.notifications.onClosed.addListener((notificationId) => {
      if (closeCallbacks[notificationId]) {
        closeCallbacks[notificationId]();
      }
      // Clean up all callbacks
      delete buttonCallbacks[notificationId];
      delete clickCallbacks[notificationId];
      delete closeCallbacks[notificationId];
    });
  }
}

/**
 * Show a Chrome notification (extension) or browser notification (web)
 * @param options Notification options
 * @returns Promise with notification ID or null if not supported
 */
export const showNotification = (options: NotificationOptions): Promise<string | null> => {
  // Prefer Chrome notifications if in extension context
  if (isExtension && chrome.notifications) {
    return new Promise((resolve) => {
      const notificationId = `onespace-notification-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
      
      // Ensure iconUrl is always provided
      const iconUrl = options.iconUrl || DEFAULT_ICON_URL;
      
      // Create the notification options with proper typing
      const notificationOptions: ChromeNotificationOptions = {
        type: options.type || 'basic',
        title: options.title,
        message: options.message,
        iconUrl: iconUrl,
        buttons: options.buttons,
        imageUrl: options.imageUrl,
        priority: options.priority || 0,
        requireInteraction: true
      };

      // Store callbacks if provided
      if (options.onButtonClick) {
        buttonCallbacks[notificationId] = options.onButtonClick;
      }
      if (options.onClicked) {
        clickCallbacks[notificationId] = options.onClicked;
      }
      if (options.onClosed) {
        closeCallbacks[notificationId] = options.onClosed;
      }

      // Cast to any to avoid TypeScript issues with the Chrome types
      chrome.notifications.create(notificationId, notificationOptions as any, (id) => {
        resolve(id);
      });
    });
  }
  
  // Fall back to browser notifications
  return showBrowserNotification(options);
};

/**
 * Clear a specific notification
 * @param notificationId ID of the notification to clear
 */
export const clearNotification = (notificationId: string): Promise<boolean> => {
  if (isExtension && chrome.notifications) {
    return new Promise((resolve) => {
      chrome.notifications.clear(notificationId, (wasCleared) => {
        resolve(wasCleared);
      });
    });
  }
  
  // For browser notifications, we can't clear specific ones by ID
  // but we can try to close all notifications with the same tag
  console.log('Cannot clear specific browser notification:', notificationId);
  return Promise.resolve(false);
};

/**
 * Clear all notifications
 */
export const clearAllNotifications = (): Promise<void> => {
  if (isExtension && chrome.notifications) {
    return new Promise((resolve) => {
      chrome.notifications.getAll((notifications) => {
        const clearPromises = Object.keys(notifications).map(id => clearNotification(id));
        Promise.all(clearPromises).then(() => resolve());
      });
    });
  }
  
  // For browser notifications, we can't clear all programmatically
  console.log('Cannot clear all browser notifications programmatically');
  return Promise.resolve();
};

/**
 * Show a spotlight notification using Chrome notifications or browser notifications
 * @param title Notification title
 * @param message Notification message
 * @param actionUrl Optional URL to open when clicked
 * @returns Promise with notification ID
 */
export const showSpotlightNotification = (
  title: string, 
  message: string, 
  actionUrl?: string
): Promise<string | null> => {
  return showNotification({
    title,
    message,
    type: 'basic',
    priority: 2,
    buttons: actionUrl ? [{ title: 'View' }] : undefined,
    onButtonClick: actionUrl ? () => window.open(actionUrl, '_blank') : undefined,
    onClicked: actionUrl ? () => window.open(actionUrl, '_blank') : undefined
  });
};

// Default export
export default {
  showNotification,
  clearNotification,
  clearAllNotifications,
  showSpotlightNotification,
  requestNotificationPermission,
  isExtension,
  isBrowserNotificationSupported
}; 